import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

/** Which graph node the detail panel is describing. The graph carries two node kinds. */
export type SelectedFlowNode =
  | { kind: 'person'; id: number; data: Record<string, unknown> }
  | { kind: 'transaction'; id: number; data: Record<string, unknown> };

export interface OwnershipUiState {
  selectedNode: SelectedFlowNode | null;
  isDetailPanelOpen: boolean;
  hasUnsavedChanges: boolean;
  modalDraftDirty: boolean;
}

const initialState: OwnershipUiState = {
  selectedNode: null,
  isDetailPanelOpen: false,
  hasUnsavedChanges: false,
  modalDraftDirty: false
};

/**
 * The ownership tab's UI state ONLY — selection, the panel, and the two dirty flags. The
 * chain, the extraction progress and the poll that used to sit beside them in a zustand
 * store are RTK Query's job now: `pollingInterval` on `getOwnershipProgress` replaces the
 * hand-rolled `setInterval`, which is what leaked a timer when the tab unmounted mid-run.
 */
export const ownershipUiSlice = createSlice({
  name: 'ownershipUi',
  initialState,
  reducers: {
    nodeSelected(state, action: PayloadAction<SelectedFlowNode>) {
      state.selectedNode = action.payload;
      state.isDetailPanelOpen = true;
    },
    detailPanelClosed(state) {
      state.selectedNode = null;
      state.isDetailPanelOpen = false;
    },
    unsavedChangesSet(state, action: PayloadAction<boolean>) {
      state.hasUnsavedChanges = action.payload;
    },
    modalDraftDirtySet(state, action: PayloadAction<boolean>) {
      state.modalDraftDirty = action.payload;
    },
    /** Leaving the tab, or switching property: nothing above survives the move. */
    ownershipUiReset() {
      return initialState;
    }
  }
});

export const {
  nodeSelected,
  detailPanelClosed,
  unsavedChangesSet,
  modalDraftDirtySet,
  ownershipUiReset
} = ownershipUiSlice.actions;

export const ownershipUiReducer = ownershipUiSlice.reducer;

/** The key this slice must be mounted under in `core/store/rootReducer.ts`. */
export const OWNERSHIP_UI_KEY = 'ownershipUi';

export interface OwnershipUiRootState {
  ownershipUi: OwnershipUiState;
}

/**
 * Module-local because the slice lives in the module, not in `core/store` — `RootState` is
 * the store's shape and only the shell may widen it. Callers state the branch they read.
 */
export function useOwnershipUi<T>(selector: (ui: OwnershipUiState) => T): T {
  return useSelector((root: OwnershipUiRootState) => selector(root.ownershipUi));
}
